// app/screens/business/OverviewScreen.js - Fully Integrated Overview
import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, RefreshControl, Alert, ActivityIndicator, StatusBar, Image } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { MaterialCommunityIcons, Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

const PRIMARY = '#0e4e96'; const SECONDARY = '#1e7be2';
const API_BASE_URL = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const api = axios.create({ baseURL: API_BASE_URL, timeout: 15000, headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-API-Key': API_KEY } });
api.interceptors.request.use(async c => { try { const t = await AsyncStorage.getItem('auth_token')||await AsyncStorage.getItem('token'); if(t)c.headers.Authorization=`Bearer ${t}`; }catch{} return c; });

const EP = {
  companyProfile:(id)=>`/api/company-profile/${id}`, jobs:'/api/jobs', applications:'/api/applications',
  interviews:'/api/interviews', projects:'/api/projects', clients:'/api/clients', vendors:'/api/vendors',
  contracts:'/api/contracts', invoices:'/api/invoices', campaigns:'/api/campaigns', employees:'/api/employees',
  teamMembers:'/api/team/members', deals:'/api/data/crm_deals', contacts:'/api/data/crm_contacts',
  activities:'/api/data/crm_activities', offers:'/api/offers', revenue:'/api/financial/revenue',
};
const getBusinessId=async()=>{try{return await AsyncStorage.getItem('company_id')||await AsyncStorage.getItem('company_id')||'0';}catch{return '0';}};
const safeLen=(res)=>(res?.data?.success&&Array.isArray(res.data.data))?res.data.data.length:0;
const safeData=(res)=>(res?.data?.success&&Array.isArray(res.data.data))?res.data.data:[];

const OverviewScreen = ({ navigation }) => {
  const [refreshing, setRefreshing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [dashboardData, setDashboardData] = useState({
    activeJobs:0, totalApplications:0, scheduledInterviews:0, activeProjects:0,
    totalClients:0, totalVendors:0, pendingInvoices:0, pendingInvoiceValue:0,
    teamMembers:0, employees:0, activeContracts:0, activeCampaigns:0,
    totalDeals:0, dealsPipelineValue:0, pendingOffers:0, totalContacts:0,
    totalRevenue:0, recentActivity:[]
  });

  useEffect(()=>{loadAll();},[]);

  const loadAll=async()=>{
    try{
      setIsLoading(true);
      const bizId=await getBusinessId();
      setCompanyId(bizId);
      const cid=parseInt(bizId)||0;
      const p={company_id:cid};

      // Fetch company profile
      try{
        const profRes=await api.get(EP.companyProfile(cid));
        const prof=profRes?.data?.success?(Array.isArray(profRes.data.data)?profRes.data.data[0]:profRes.data.data):null;
        if(prof){setCompanyProfile(prof);await AsyncStorage.setItem('company_profile',JSON.stringify(prof));}
      }catch{try{const c=await AsyncStorage.getItem('company_profile');if(c)setCompanyProfile(JSON.parse(c));}catch{}}

      // Fetch all data in parallel
      const [jobsR,appsR,intR,projR,clientsR,vendorsR,contractsR,invoicesR,campaignsR,empR,teamR,dealsR,contactsR,actsR,offersR]=await Promise.allSettled([
        api.get(EP.jobs,{params:{...p,status:'active'}}).catch(()=>({data:{success:false}})),
        api.get(EP.applications,{params:p}).catch(()=>({data:{success:false}})),
        api.get(EP.interviews,{params:{...p,scheduled:1}}).catch(()=>({data:{success:false}})),
        api.get(EP.projects,{params:{...p,status:'active'}}).catch(()=>({data:{success:false}})),
        api.get(EP.clients,{params:p}).catch(()=>({data:{success:false}})),
        api.get(EP.vendors,{params:p}).catch(()=>({data:{success:false}})),
        api.get(EP.contracts,{params:{...p,status:'active'}}).catch(()=>({data:{success:false}})),
        api.get(EP.invoices,{params:p}).catch(()=>({data:{success:false}})),
        api.get(EP.campaigns,{params:{...p,status:'active'}}).catch(()=>({data:{success:false}})),
        api.get(EP.employees,{params:p}).catch(()=>({data:{success:false}})),
        api.get(EP.teamMembers,{params:p}).catch(()=>({data:{success:false}})),
        api.get(EP.deals,{params:p}).catch(()=>({data:{success:false}})),
        api.get(EP.contacts,{params:p}).catch(()=>({data:{success:false}})),
        api.get(EP.activities,{params:p}).catch(()=>({data:{success:false}})),
        api.get(EP.offers,{params:{...p,status:'pending'}}).catch(()=>({data:{success:false}})),
      ]);

      const g=r=>r.status==='fulfilled'?r.value:null;
      const invoices=safeData(g(invoicesR));
      const pendingInv=invoices.filter(i=>['pending','sent','overdue'].includes(i.status));
      const paidInv=invoices.filter(i=>i.status==='paid');
      const deals=safeData(g(dealsR));
      const acts=safeData(g(actsR));

      // Build recent activity from activities + invoices
      const recent=[];
      acts.slice(0,5).forEach(a=>recent.push({id:`act-${a.id}`,type:a.type||'activity',title:a.title||'Activity',desc:a.description||'',time:a.created_at,icon:a.type==='call'?'phone':a.type==='email'?'email':'account-group',color:a.type==='call'?'#3B82F6':'#10B981'}));
      invoices.slice(0,3).forEach(i=>recent.push({id:`inv-${i.id}`,type:'invoice',title:`Invoice ${i.invoice_number||''}`,desc:`${i.client_name||''} - £${parseFloat(i.total||0).toFixed(0)}`,time:i.created_at,icon:i.status==='paid'?'check-circle':'file-document',color:i.status==='paid'?'#10B981':'#F59E0B'}));
      recent.sort((a,b)=>new Date(b.time||0)-new Date(a.time||0));

      setDashboardData({
        activeJobs:safeLen(g(jobsR)), totalApplications:safeLen(g(appsR)),
        scheduledInterviews:safeLen(g(intR)), activeProjects:safeLen(g(projR)),
        totalClients:safeLen(g(clientsR)), totalVendors:safeLen(g(vendorsR)),
        activeContracts:safeLen(g(contractsR)), pendingInvoices:pendingInv.length,
        pendingInvoiceValue:pendingInv.reduce((s,i)=>s+parseFloat(i.total||0),0),
        activeCampaigns:safeLen(g(campaignsR)),
        employees:safeLen(g(empR)), teamMembers:safeLen(g(teamR)),
        totalDeals:deals.length,
        dealsPipelineValue:deals.reduce((s,d)=>s+parseFloat(d.value||0),0),
        pendingOffers:safeLen(g(offersR)), totalContacts:safeLen(g(contactsR)),
        totalRevenue:paidInv.reduce((s,i)=>s+parseFloat(i.total||0),0),
        recentActivity:recent.slice(0,8),
      });

      // Cache
      await AsyncStorage.setItem('dashboard_data',JSON.stringify(dashboardData)).catch(()=>{});
    }catch(e){
      console.error('Overview load error:',e);
      try{const c=await AsyncStorage.getItem('dashboard_data');if(c)setDashboardData(JSON.parse(c));}catch{}
    }finally{setIsLoading(false);}
  };

  const onRefresh=async()=>{setRefreshing(true);await loadAll();setRefreshing(false);};
  const d=dashboardData;
  const formatCurrency=v=>new Intl.NumberFormat('en-GB',{style:'currency',currency:companyProfile?.currency||'GBP',minimumFractionDigits:0,maximumFractionDigits:0}).format(v||0);
  const timeAgo=t=>{if(!t)return '';const s=Math.floor((Date.now()-new Date(t).getTime())/1000);if(s<60)return 'now';if(s<3600)return `${Math.floor(s/60)}m ago`;if(s<86400)return `${Math.floor(s/3600)}h ago`;return `${Math.floor(s/86400)}d ago`;};

  const quickActions=[
    {title:'Post Job',icon:'briefcase-plus',screen:'CreateJobScreen',color:'#3B82F6'},
    {title:'Add Client',icon:'account-plus',screen:'ClientsScreen',color:'#10B981'},
    {title:'New Invoice',icon:'file-document-plus',screen:'InvoiceSystemScreen',color:'#F59E0B'},
    {title:'Add Employee',icon:'account-group',screen:'AddEmployeeScreen',color:'#8B5CF6'},
    {title:'New Project',icon:'folder-plus',screen:'ProjectListScreen',color:'#EC4899'},
    {title:'CRM',icon:'account-tie',screen:'CRMScreen',color:'#EF4444'},
  ];

  const statCards=[
    {title:'Active Jobs',value:d.activeJobs,icon:'briefcase',color:'#3B82F6',screen:'JobManagementScreen'},
    {title:'Applications',value:d.totalApplications,icon:'file-account',color:'#8B5CF6',screen:'ApplicantsPoolScreen'},
    {title:'Interviews',value:d.scheduledInterviews,icon:'calendar-clock',color:'#EC4899',screen:'BusinessTestsInterviewsScreen'},
    {title:'Projects',value:d.activeProjects,icon:'folder-star',color:'#F59E0B',screen:'ProjectListScreen'},
    {title:'Clients',value:d.totalClients,icon:'account-tie',color:'#10B981',screen:'CRMScreen'},
    {title:'Employees',value:d.employees,icon:'account-group',color:PRIMARY,screen:'EmployeeManagementScreen'},
    {title:'Deals',value:d.totalDeals,icon:'handshake',color:'#6366F1',screen:'OpportunityScreen'},
    {title:'Pipeline',value:formatCurrency(d.dealsPipelineValue),icon:'pipe',color:'#0EA5E9',screen:'PipelineManagementScreen'},
    {title:'Pending Invoices',value:d.pendingInvoices,icon:'receipt',color:'#F97316',screen:'InvoiceSystemScreen'},
    {title:'Revenue',value:formatCurrency(d.totalRevenue),icon:'cash',color:'#22C55E',screen:'FinancialManagementScreen'},
    {title:'Contracts',value:d.activeContracts,icon:'file-sign',color:'#14B8A6',screen:'CreateNewContractScreen'},
    {title:'Campaigns',value:d.activeCampaigns,icon:'bullhorn',color:'#F43F5E',screen:'CampaignListScreen'},
  ];

  if(isLoading)return <SafeAreaView style={s.loading}><StatusBar barStyle="dark-content"/><ActivityIndicator size="large" color={PRIMARY}/><Text style={s.loadingTxt}>Loading overview...</Text></SafeAreaView>;

  return (
    <SafeAreaView style={s.container}>
      <StatusBar barStyle="light-content"/>
      {/* Header */}
      <View style={s.header}>
        <TouchableOpacity onPress={()=>navigation?.goBack()} style={s.hBtn}><Ionicons name="chevron-back" size={24} color="#FFF"/></TouchableOpacity>
        <View style={s.hCenter}>
          {companyProfile?.logo?<Image source={{ uri: (companyProfile.logo && companyProfile.logo.startsWith('http')) ? companyProfile.logo : undefined }} style={s.hLogo}/>:<MaterialCommunityIcons name="office-building" size={28} color="#FFF"/>}
          <View style={{marginLeft:10}}>
            <Text style={s.hTitle}>{companyProfile?.name||'Business Overview'}</Text>
            <Text style={s.hSub}>{companyProfile?.industry?`${companyProfile.industry} · `:''}{companyProfile?.headquarters||''}</Text>
          </View>
        </View>
        <TouchableOpacity onPress={()=>navigation?.navigate?.('CompanyProfileScreen')} style={s.hBtn}><MaterialCommunityIcons name="cog" size={22} color="#FFF"/></TouchableOpacity>
      </View>

      <ScrollView refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={[PRIMARY]}/>} showsVerticalScrollIndicator={false}>
        {/* Company info bar */}
        {companyProfile&&(
          <View style={s.companyBar}>
            {companyProfile.employee_count&&<View style={s.chip}><MaterialCommunityIcons name="account-group" size={14} color={PRIMARY}/><Text style={s.chipTxt}>{companyProfile.employee_count} employees</Text></View>}
            {companyProfile.website&&<View style={s.chip}><MaterialCommunityIcons name="web" size={14} color={PRIMARY}/><Text style={s.chipTxt}>{companyProfile.website}</Text></View>}
            {companyProfile.vat_number&&<View style={s.chip}><MaterialCommunityIcons name="card-account-details" size={14} color={PRIMARY}/><Text style={s.chipTxt}>VAT: {companyProfile.vat_number}</Text></View>}
          </View>
        )}

        {/* Quick Actions */}
        <View style={s.section}>
          <Text style={s.secTitle}>Quick Actions</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            {quickActions.map((a,i)=>(
              <TouchableOpacity key={i} style={s.quickAction} onPress={()=>navigation?.navigate?.(a.screen)}>
                <View style={[s.qaIcon,{backgroundColor:a.color}]}><MaterialCommunityIcons name={a.icon} size={22} color="#FFF"/></View>
                <Text style={s.qaText}>{a.title}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>

        {/* Stats Grid */}
        <View style={s.section}>
          <Text style={s.secTitle}>Dashboard</Text>
          <View style={s.grid}>
            {statCards.map((c,i)=>(
              <TouchableOpacity key={i} style={s.statCard} onPress={()=>c.screen&&navigation?.navigate?.(c.screen)}>
                <View style={[s.statIcon,{backgroundColor:c.color+'15'}]}><MaterialCommunityIcons name={c.icon} size={22} color={c.color}/></View>
                <Text style={[s.statValue,{color:c.color}]}>{c.value}</Text>
                <Text style={s.statLabel}>{c.title}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        {/* Financial Summary */}
        <View style={s.section}>
          <Text style={s.secTitle}>Financial Summary</Text>
          <View style={s.finRow}>
            <View style={s.finCard}><Text style={s.finLabel}>Revenue</Text><Text style={[s.finValue,{color:'#10B981'}]}>{formatCurrency(d.totalRevenue)}</Text></View>
            <View style={s.finCard}><Text style={s.finLabel}>Pending</Text><Text style={[s.finValue,{color:'#F59E0B'}]}>{formatCurrency(d.pendingInvoiceValue)}</Text></View>
            <View style={s.finCard}><Text style={s.finLabel}>Pipeline</Text><Text style={[s.finValue,{color:'#3B82F6'}]}>{formatCurrency(d.dealsPipelineValue)}</Text></View>
          </View>
        </View>

        {/* Recent Activity */}
        <View style={s.section}>
          <Text style={s.secTitle}>Recent Activity</Text>
          {d.recentActivity.length===0?<Text style={s.emptyTxt}>No recent activity</Text>:
            d.recentActivity.map((a,i)=>(
              <View key={i} style={s.actCard}>
                <View style={[s.actIcon,{backgroundColor:(a.color||'#6B7280')+'15'}]}><MaterialCommunityIcons name={a.icon||'circle'} size={18} color={a.color||'#6B7280'}/></View>
                <View style={{flex:1}}>
                  <Text style={s.actTitle}>{a.title}</Text>
                  {a.desc?<Text style={s.actDesc}>{a.desc}</Text>:null}
                </View>
                <Text style={s.actTime}>{timeAgo(a.time)}</Text>
              </View>
            ))
          }
        </View>

        {/* Team */}
        <View style={s.section}>
          <Text style={s.secTitle}>Team Overview</Text>
          <View style={s.teamRow}>
            <View style={s.teamCard}><MaterialCommunityIcons name="account-group" size={24} color={PRIMARY}/><Text style={s.teamVal}>{d.employees}</Text><Text style={s.teamLbl}>Employees</Text></View>
            <View style={s.teamCard}><MaterialCommunityIcons name="account-multiple" size={24} color="#8B5CF6"/><Text style={s.teamVal}>{d.teamMembers}</Text><Text style={s.teamLbl}>Team Members</Text></View>
            <View style={s.teamCard}><MaterialCommunityIcons name="contacts" size={24} color="#F59E0B"/><Text style={s.teamVal}>{d.totalContacts}</Text><Text style={s.teamLbl}>CRM Contacts</Text></View>
          </View>
        </View>

        <View style={{height:40}}/>
      </ScrollView>
    </SafeAreaView>
  );
};

const s=StyleSheet.create({
  container:{flex:1,backgroundColor:'#F3F4F6'},loading:{flex:1,justifyContent:'center',alignItems:'center',backgroundColor:'#F3F4F6'},loadingTxt:{fontSize:16,color:'#6B7280',marginTop:16},
  header:{backgroundColor:PRIMARY,paddingVertical:14,paddingHorizontal:16,flexDirection:'row',alignItems:'center'},
  hBtn:{width:36,height:36,borderRadius:18,backgroundColor:'rgba(255,255,255,0.12)',justifyContent:'center',alignItems:'center'},
  hCenter:{flex:1,flexDirection:'row',alignItems:'center',marginLeft:10},hLogo:{width:32,height:32,borderRadius:16},
  hTitle:{color:'#FFF',fontSize:18,fontWeight:'bold'},hSub:{color:'#93C5FD',fontSize:12,marginTop:1},
  companyBar:{flexDirection:'row',flexWrap:'wrap',backgroundColor:'#FFF',padding:12,gap:8,borderBottomWidth:1,borderBottomColor:'#E5E7EB'},
  chip:{flexDirection:'row',alignItems:'center',backgroundColor:PRIMARY+'10',borderRadius:12,paddingHorizontal:10,paddingVertical:4},chipTxt:{color:PRIMARY,fontSize:12,marginLeft:4,fontWeight:'500'},
  section:{backgroundColor:'#FFF',margin:12,marginBottom:0,padding:16,borderRadius:12,borderWidth:1,borderColor:'#E5E7EB'},
  secTitle:{fontSize:17,fontWeight:'bold',color:'#1F2937',marginBottom:12},
  quickAction:{alignItems:'center',marginRight:16,width:68},qaIcon:{width:48,height:48,borderRadius:24,justifyContent:'center',alignItems:'center'},qaText:{fontSize:11,color:'#6B7280',marginTop:6,textAlign:'center',fontWeight:'500'},
  grid:{flexDirection:'row',flexWrap:'wrap',justifyContent:'space-between'},
  statCard:{width:'31%',backgroundColor:'#F9FAFB',borderRadius:10,padding:12,alignItems:'center',marginBottom:10},
  statIcon:{width:40,height:40,borderRadius:20,justifyContent:'center',alignItems:'center',marginBottom:6},
  statValue:{fontSize:18,fontWeight:'bold',marginBottom:2},statLabel:{fontSize:11,color:'#6B7280',textAlign:'center'},
  finRow:{flexDirection:'row',justifyContent:'space-between'},finCard:{flex:1,backgroundColor:'#F9FAFB',borderRadius:10,padding:12,alignItems:'center',marginHorizontal:2},
  finLabel:{fontSize:12,color:'#6B7280',marginBottom:4},finValue:{fontSize:16,fontWeight:'bold'},
  actCard:{flexDirection:'row',alignItems:'center',paddingVertical:10,borderBottomWidth:1,borderBottomColor:'#F3F4F6'},
  actIcon:{width:32,height:32,borderRadius:16,justifyContent:'center',alignItems:'center',marginRight:10},
  actTitle:{fontSize:14,fontWeight:'600',color:'#1F2937'},actDesc:{fontSize:12,color:'#6B7280',marginTop:1},actTime:{fontSize:11,color:'#9CA3AF'},
  emptyTxt:{fontSize:14,color:'#9CA3AF',textAlign:'center',padding:16},
  teamRow:{flexDirection:'row',justifyContent:'space-between'},teamCard:{flex:1,backgroundColor:'#F9FAFB',borderRadius:10,padding:12,alignItems:'center',marginHorizontal:2},
  teamVal:{fontSize:20,fontWeight:'bold',color:'#1F2937',marginTop:4},teamLbl:{fontSize:11,color:'#6B7280',marginTop:2},
});

export default OverviewScreen;